import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loader } from "@/components/dashboard/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ImageFocalField } from "@/components/admin/MediaPicker";

function Field({ label, value, onChange, textarea }) {
  return (
    <div><Label className="text-xs">{label}</Label>
      {textarea ? <Textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" /> : <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" />}
    </div>
  );
}

export default function AdminSite() {
  const [content, setContent] = useState(null);
  const [faq, setFaq] = useState([]);
  const [nav, setNav] = useState([]);
  const load = () => {
    api.get("/website/content").then(({ data }) => setContent(data || {}));
    api.get("/website/faq/all").then(({ data }) => setFaq(data || []));
    api.get("/website/nav").then(({ data }) => setNav(data || []));
  };
  useEffect(() => { load(); }, []);

  const setSec = (sec, key, val) => setContent((c) => ({ ...c, [sec]: { ...c[sec], [key]: val } }));
  const setImage = (key, val) => setContent((c) => ({ ...c, images: { ...(c.images || {}), [key]: val } }));
  const setImagePos = (key, val) => setContent((c) => ({ ...c, images_pos: { ...(c.images_pos || {}), [key]: val } }));
  const setCompany = (key, val) => setContent((c) => ({ ...c, company: { ...(c.company || {}), [key]: val } }));
  const saveContent = async () => { try { await api.put("/website/content", content); toast.success("Contenu enregistré"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const saveNav = async () => { try { await api.put("/website/nav", { items: nav }); toast.success("Navigation enregistrée"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const addFaq = async () => { const { data } = await api.post("/website/faq", { question: "Nouvelle question", answer: "", category: "Général" }); setFaq((f) => [...f, data]); };
  const saveFaq = async (item) => { await api.put(`/website/faq/${item.faq_id}`, item); toast.success("FAQ enregistrée"); };
  const delFaq = async (id) => { await api.delete(`/website/faq/${id}`); setFaq((f) => f.filter((x) => x.faq_id !== id)); };

  if (content === null) return <Loader />;
  const hero = content.hero || {};
  return (
    <div>
      <PageHeader title="Site internet" subtitle="Modifiez le contenu public sans toucher au code." />
      <Tabs defaultValue="content">
        <TabsList><TabsTrigger value="content" data-testid="tab-content">Contenu</TabsTrigger><TabsTrigger value="faq" data-testid="tab-faq">FAQ</TabsTrigger><TabsTrigger value="nav" data-testid="tab-nav">Navigation</TabsTrigger><TabsTrigger value="footer" data-testid="tab-footer">Pied de page</TabsTrigger></TabsList>

        <TabsContent value="content" className="mt-4 space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-3">Section Héros (accueil)</h3>
            <div className="grid gap-3">
              <Field label="Surtitre" value={hero.overline} onChange={(v) => setSec("hero", "overline", v)} />
              <Field label="Titre principal" value={hero.title} onChange={(v) => setSec("hero", "title", v)} textarea />
              <Field label="Sous-titre" value={hero.subtitle} onChange={(v) => setSec("hero", "subtitle", v)} textarea />
              <div className="grid grid-cols-3 gap-3">
                <Field label="CTA 1" value={hero.cta_primary} onChange={(v) => setSec("hero", "cta_primary", v)} />
                <Field label="CTA 2" value={hero.cta_secondary} onChange={(v) => setSec("hero", "cta_secondary", v)} />
                <Field label="CTA 3" value={hero.cta_tertiary} onChange={(v) => setSec("hero", "cta_tertiary", v)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 border-t border-slate-100">
                <ImageFocalField testId="focal-hero" label="Image du héros" value={content.images?.hero} onChange={(v) => setImage("hero", v)}
                  pos={content.images_pos?.hero} onPos={(v) => setImagePos("hero", v)} />
                <ImageFocalField testId="focal-benefit" label="Image section avantages" value={content.images?.benefit} onChange={(v) => setImage("benefit", v)}
                  pos={content.images_pos?.benefit} onPos={(v) => setImagePos("benefit", v)} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-1">Images des pages</h3>
            <p className="text-xs text-slate-400 mb-4">Image principale des pages « Pour les médecins », « Pour les cabinets » et « Installation en Suisse ». Sélectionnez depuis Médias ou téléversez une nouvelle image.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <ImageFocalField testId="focal-info-medecins" label="Page « Pour les médecins »" value={content.images?.info_medecins} onChange={(v) => setImage("info_medecins", v)}
                pos={content.images_pos?.info_medecins} onPos={(v) => setImagePos("info_medecins", v)} />
              <ImageFocalField testId="focal-info-cabinets" label="Page « Pour les cabinets »" value={content.images?.info_cabinets} onChange={(v) => setImage("info_cabinets", v)}
                pos={content.images_pos?.info_cabinets} onPos={(v) => setImagePos("info_cabinets", v)} />
              <ImageFocalField testId="focal-info-installation" label="Page « Installation en Suisse »" value={content.images?.info_installation} onChange={(v) => setImage("info_installation", v)}
                pos={content.images_pos?.info_installation} onPos={(v) => setImagePos("info_installation", v)} />
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-1">Contact & coordonnées de l'entreprise</h3>
            <p className="text-xs text-slate-400 mb-4">Colonne de gauche de la page Contact. L'e-mail, le téléphone et l'adresse sont partagés avec le pied de page (modifiez-les une seule fois).</p>
            <div className="grid gap-3">
              <Field label="Petit titre / surtitre" value={content.contact?.overline ?? "Contact"} onChange={(v) => setSec("contact", "overline", v)} />
              <Field label="Titre principal" value={content.contact?.title} onChange={(v) => setSec("contact", "title", v)} />
              <Field label="Texte descriptif" value={content.contact?.description ?? ""} onChange={(v) => setSec("contact", "description", v)} textarea />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-slate-100">
                <Field label="Adresse e-mail" value={content.company?.email ?? content.footer?.email} onChange={(v) => setCompany("email", v)} />
                <Field label="Numéro de téléphone" value={content.company?.phone ?? content.footer?.phone} onChange={(v) => setCompany("phone", v)} />
                <Field label="Adresse / ville / localisation" value={content.company?.address ?? content.footer?.address} onChange={(v) => setCompany("address", v)} />
              </div>
            </div>
          </div>
          {["how", "advantages", "practices", "opening", "office", "installation", "team", "faq"].map((sec) => (
            <div key={sec} className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900 capitalize">{content[sec]?.title || sec}</h3>
                <label className="flex items-center gap-2 text-sm text-slate-500">Visible <Switch checked={content[sec]?.visible !== false} onCheckedChange={(v) => setSec(sec, "visible", v)} /></label>
              </div>
              <div className="grid gap-3">
                <Field label="Titre" value={content[sec]?.title} onChange={(v) => setSec(sec, "title", v)} />
                <Field label="Sous-titre" value={content[sec]?.subtitle} onChange={(v) => setSec(sec, "subtitle", v)} textarea />
              </div>
            </div>
          ))}
          <Button className="bg-primary" onClick={saveContent} data-testid="save-content"><Save className="h-4 w-4 mr-1.5" />Enregistrer le contenu</Button>
        </TabsContent>

        <TabsContent value="faq" className="mt-4 space-y-3">
          {faq.map((item, i) => (
            <div key={item.faq_id} className="bg-white rounded-xl border border-slate-200 p-4 space-y-2" data-testid={`admin-faq-${item.faq_id}`}>
              <Input value={item.question} onChange={(e) => setFaq((f) => f.map((x, idx) => idx === i ? { ...x, question: e.target.value } : x))} />
              <Textarea rows={2} value={item.answer} onChange={(e) => setFaq((f) => f.map((x, idx) => idx === i ? { ...x, answer: e.target.value } : x))} />
              <div className="flex items-center gap-2">
                <Input className="w-40" value={item.category} onChange={(e) => setFaq((f) => f.map((x, idx) => idx === i ? { ...x, category: e.target.value } : x))} />
                <label className="flex items-center gap-2 text-sm text-slate-500">Actif <Switch checked={item.active} onCheckedChange={(v) => setFaq((f) => f.map((x, idx) => idx === i ? { ...x, active: v } : x))} /></label>
                <div className="flex-1" />
                <Button size="sm" variant="outline" onClick={() => saveFaq(faq[i])}>Enregistrer</Button>
                <button onClick={() => delFaq(item.faq_id)}><Trash2 className="h-4 w-4 text-rose-400 hover:text-rose-600" /></button>
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addFaq} data-testid="add-faq"><Plus className="h-4 w-4 mr-1.5" />Ajouter une question</Button>
        </TabsContent>

        <TabsContent value="nav" className="mt-4 space-y-2">
          {nav.map((n, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3">
              <Input className="w-52" value={n.label} onChange={(e) => setNav((a) => a.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} />
              <Input className="flex-1" value={n.path} onChange={(e) => setNav((a) => a.map((x, idx) => idx === i ? { ...x, path: e.target.value } : x))} />
              <label className="flex items-center gap-2 text-sm text-slate-500">Visible <Switch checked={n.visible} onCheckedChange={(v) => setNav((a) => a.map((x, idx) => idx === i ? { ...x, visible: v } : x))} /></label>
            </div>
          ))}
          <Button className="bg-primary" onClick={saveNav} data-testid="save-nav"><Save className="h-4 w-4 mr-1.5" />Enregistrer la navigation</Button>
        </TabsContent>

        <TabsContent value="footer" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5 grid gap-3 max-w-lg">
            <Field label="Tagline" value={content.footer?.tagline} onChange={(v) => setSec("footer", "tagline", v)} />
            <Field label="E-mail" value={content.footer?.email} onChange={(v) => setSec("footer", "email", v)} />
            <Field label="Téléphone" value={content.footer?.phone} onChange={(v) => setSec("footer", "phone", v)} />
            <Field label="Adresse" value={content.footer?.address} onChange={(v) => setSec("footer", "address", v)} />
            <Button className="bg-primary w-fit" onClick={saveContent}><Save className="h-4 w-4 mr-1.5" />Enregistrer</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
